import React, { useEffect, useState } from "react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { getRequests } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const serviceTypeOptions = [
  { value: "", label: "Todos" },
  { value: "visa", label: "Visto" },
  { value: "translation", label: "Tradução" },
  { value: "authentication", label: "Autenticação" },
  { value: "passport", label: "Passaporte" },
  { value: "flight", label: "Voo" },
  { value: "insurance", label: "Seguro" },
];

export const AdminRequestsPage = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    service_type: "",
    language: "",
  });
  const [items, setItems] = useState([]);

  const fetchData = async (currentFilters) => {
    const params = {};
    if (currentFilters.service_type) params.service_type = currentFilters.service_type;
    if (currentFilters.language) params.language = currentFilters.language;
    const data = await getRequests(params);
    return data;
  };

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      const data = await fetchData(filters);
      if (isMounted) {
        setItems(data);
      }
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [filters]);

  return (
    <PageShell>
      <PageHeader
        title={t("admin.pageTitle")}
        subtitle={t("admin.pageSubtitle")}
        testIdPrefix="admin-page-header"
      />

      <div className="mb-4 flex flex-wrap gap-4 items-end" data-testid="admin-filters">
        <div className="w-40">
          <label className="text-xs text-slate-300 block mb-1">
            {t("admin.filters.serviceType")}
          </label>
          <Select
            value={filters.service_type}
            onValueChange={(val) =>
              setFilters((prev) => ({ ...prev, service_type: val }))
            }
          >
            <SelectTrigger
              className="h-8 bg-slate-900 border-slate-800 text-xs"
              data-testid="admin-filter-service-type"
            >
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-xs">
              {serviceTypeOptions.map((opt) => (
                <SelectItem
                  key={opt.value || "all"}
                  value={opt.value}
                  data-testid={`admin-filter-service-type-${opt.value || "all"}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-32">
          <label className="text-xs text-slate-300 block mb-1">
            {t("admin.filters.language")}
          </label>
          <Select
            value={filters.language}
            onValueChange={(val) =>
              setFilters((prev) => ({ ...prev, language: val }))
            }
          >
            <SelectTrigger
              className="h-8 bg-slate-900 border-slate-800 text-xs"
              data-testid="admin-filter-language"
            >
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-xs">
              <SelectItem value="all" data-testid="admin-filter-language-all">
                Todos
              </SelectItem>
              <SelectItem value="pt" data-testid="admin-filter-language-pt">
                PT
              </SelectItem>
              <SelectItem value="en" data-testid="admin-filter-language-en">
                EN
              </SelectItem>
              <SelectItem value="fr" data-testid="admin-filter-language-fr">
                FR
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="admin-requests-table-wrapper">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead data-testid="admin-th-date">
                {t("admin.table.createdAt")}
              </TableHead>
              <TableHead data-testid="admin-th-service">
                {t("admin.table.service")}
              </TableHead>
              <TableHead data-testid="admin-th-client">
                {t("admin.table.client")}
              </TableHead>
              <TableHead data-testid="admin-th-email">
                {t("admin.table.email")}
              </TableHead>
              <TableHead data-testid="admin-th-summary">
                {t("admin.table.summary")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="admin-tbody">
            {items.map((item) => {
              const created = item.created_at
                ? new Date(item.created_at).toLocaleString()
                : "";
              const summary =
                item.service_type === "visa"
                  ? `${item.destination_country || ""} · ${item.visa_subtype || ""}`
                  : item.service_type;
              return (
                <TableRow
                  key={item.id}
                  data-testid={`admin-row-${item.id}`}
                >
                  <TableCell>{created}</TableCell>
                  <TableCell>{item.service_type}</TableCell>
                  <TableCell>{item.contact?.full_name}</TableCell>
                  <TableCell>{item.contact?.email}</TableCell>
                  <TableCell>{summary}</TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow data-testid="admin-empty-state-row">
                <TableCell colSpan={5} className="text-center text-xs text-slate-400">
                  Nenhum pedido encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </PageShell>
  );
};

export default AdminRequestsPage;
